import {shallowMemoize} from '@sail/utils';
import {NavigationError} from 'src/errors';
import toTree, {type RouteMatchTree} from 'src/internal/compiler/toTree';
import groupSeparator from 'src/internal/groupSeparator';
import memoizeConfig from 'src/internal/memoizeConfig';
import urlToLocation from 'src/internal/urlToLocation';

import type {
  BaseParamsLocation,
  BaseParamsPathToRegexp,
  NavigationConfigInternal,
  RouteNames,
  RouteResult,
} from 'src/types';

export type Routes = {[K in RouteNames]?: RouteResult<K>};

export function transformParams(
  params: BaseParamsPathToRegexp | object,
): BaseParamsLocation {
  const paramsPathToRegexp: BaseParamsPathToRegexp = {...params};
  const paramsLocation: BaseParamsLocation = {};

  for (const name in paramsPathToRegexp) {
    if (paramsPathToRegexp.hasOwnProperty(name)) {
      const separator = name.indexOf(groupSeparator);
      const value = paramsPathToRegexp[name];

      if (separator === -1) {
        if (paramsLocation.hasOwnProperty(name)) {
          throw new NavigationError(
            `Argument name collision in route definition for ${name}`,
          );
        }

        paramsLocation[name] = value;
      } else {
        const key = name.substr(0, separator);
        const sub = name.substr(separator + groupSeparator.length);

        const actualObj = paramsLocation[key];

        if (typeof actualObj === 'string' || Array.isArray(actualObj)) {
          throw new NavigationError(
            `Argument name collision in route definition ${name} ${key}`,
          );
        }

        const newObj = actualObj || {};

        newObj[sub] = value;
        paramsLocation[key] = newObj;
      }
    }
  }

  return paramsLocation;
}

const createRoutesIndexMap = shallowMemoize((routes: Array<RouteNames>) => {
  return Object.fromEntries(routes.map((route, index) => [route, index]));
});

const normalizeParams = (
  params: BaseParamsPathToRegexp,
  route: string,
): BaseParamsPathToRegexp => {
  return Object.fromEntries(
    Object.entries(params).map(([key, value]) => {
      return [key.replace(route, ''), value];
    }),
  );
};

export default memoizeConfig(function newMatch(
  config: NavigationConfigInternal,
  path: string,
) {
  // TODO: this is memoized but it is temporary since it building the tree can have an impact on TTI
  // we will be building the tree at build time and importing it
  const tree = toTree(config.routes);

  const location = urlToLocation(path);
  const parts = location.pathname.slice(1).split('/');
  const length = parts.length;

  // TODO: using the same shape of response as urlToRoutesOld for now. We can move it to an array in the future
  const finds: Routes = {};

  function isGlob(key: string): boolean {
    return key.includes('.*');
  }

  function isArrayGlob(key: string, finished: boolean): boolean {
    return key.endsWith('*') || (!finished && key.endsWith('+'));
  }

  function isRegexPattern(key: string): boolean {
    return key.includes('(');
  }

  function isStatic(part: string): boolean {
    return !!part;
  }

  function handleGlob(
    key: string,
    index: number,
    params: BaseParamsPathToRegexp,
    node: RouteMatchTree,
  ): void {
    const newKey = key.replace(/(\(\.?\*\)|\.\*)$/, '');
    const newNode = node.p?.[key];
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    newNode &&
      checkNode(
        index + parts.slice(index).length,
        {
          ...params,
          [newKey]: decodeURIComponent(parts.slice(index).join('/')),
        },
        newNode,
      );
  }

  function handleArrayGlob(
    key: string,
    index: number,
    params: BaseParamsPathToRegexp,
    node: RouteMatchTree,
    finished: boolean,
  ): void {
    const remainingParts = parts
      .slice(index)
      .filter(Boolean)
      .map(decodeURIComponent);

    // If it's a '+' we need to have at least one part
    let i = key.endsWith('*') ? 0 : 1;
    // If it did finish we need to have all parts
    if (finished) {
      i = remainingParts.length - 1;
    }
    for (i; i <= remainingParts.length; i++) {
      const globParams =
        i > 0
          ? {...params, [key.slice(0, -1)]: remainingParts.slice(0, i)}
          : params;

      const newNode = node.p?.[key];
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      newNode && checkNode(index + i, globParams, newNode);
    }
  }

  function handleRegex(
    key: string,
    index: number,
    params: BaseParamsPathToRegexp,
    node: RouteMatchTree,
    part: string,
  ): void {
    const nAndR = key.match(/^([^()]+)(\(.*\))$/);
    if (!nAndR) {
      return;
    }
    // TODO: would be cool to avoid this and have the regular expression
    // already encoded somewhere. But for now it should be OK
    const regExp = new RegExp(`^${nAndR[2]}$`);

    if (regExp.test(part)) {
      const newNode = node.p?.[key];
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      newNode &&
        checkNode(
          index + 1,
          {...params, [nAndR[1]]: decodeURIComponent(part)},
          newNode,
        );
    }
  }

  function sortFinds(): {[k: string]: RouteResult<RouteNames>} {
    const entries = Object.entries(finds);
    const routesIndexMap = createRoutesIndexMap(tree.l);

    entries.sort((a, b) => routesIndexMap[a[0]] - routesIndexMap[b[0]]);
    return Object.fromEntries(entries);
  }

  function checkNode(
    index: number,
    params: BaseParamsPathToRegexp,
    node: RouteMatchTree,
  ): void {
    const part = parts[index];
    const finished = index >= length;

    // Special case for the '/' route
    if (finished || (parts[index] === '' && index === length - 1)) {
      node.n?.forEach((routeName: RouteNames) => {
        const routeConfig = config.routes[routeName];

        const route: RouteResult<RouteNames> = {
          name: routeName,
          params: transformParams(normalizeParams(params, routeName)),
          search: location.search,
          searchSafe:
            routeConfig && typeof routeConfig !== 'string'
              ? routeConfig[1](location.search)
              : {},
          hash: location.hash,
        };

        // A route can match more than once, we keep the first match.
        if (!finds[routeName]) {
          Object.assign(finds, {[routeName]: route});
        }
      });
    }

    if (!finished && part && node?.s?.[part.toLowerCase()]) {
      checkNode(index + 1, params, node.s[part.toLowerCase()]);
    }

    for (const key in node.p) {
      if (isGlob(key)) {
        handleGlob(key, index, params, node);
      } else if (isArrayGlob(key, finished)) {
        handleArrayGlob(key, index, params, node, finished);
      } else if (!finished && isRegexPattern(key)) {
        handleRegex(key, index, params, node, part);
      } else if (!finished && isStatic(part)) {
        checkNode(
          index + 1,
          {...params, [key]: decodeURIComponent(part)},
          node.p[key],
        );
      }
    }
  }

  checkNode(0, {}, tree);

  return sortFinds();
});
